import type { ShipmentRouteBatchRow } from '@gpx-spa/queries/src/getAccountShipmentRoutesBatch';

function orderedShipmentRouteSegments(
  segments: ShipmentRouteBatchRow[],
  shipmentId: number,
): ShipmentRouteBatchRow[] {
  return segments
    .filter((s) => s.shipment_id === shipmentId)
    .sort((a, b) => a.id - b.id);
}

/**
 * True when API batch has at least one corridor-relevant path (polyline + positive width).
 * Used to skip cache lookup while routes are still loading (empty batch → one stable “empty” hash for all shipments).
 */
export function shipmentHasCorridorHashableRoutes(
  segments: ShipmentRouteBatchRow[],
  shipmentId: number,
): boolean {
  for (const seg of orderedShipmentRouteSegments(segments, shipmentId)) {
    const opts = seg.route_options ?? [];
    const width = seg.corridor_width_meters;
    for (const opt of opts) {
      if (!opt?.encoded_polyline || width == null || width <= 0) continue;
      return true;
    }
  }
  return false;
}

/**
 * Stable hash of all corridor-buffer inputs for a shipment (saved routes).
 * Must stay aligned with which paths {@link buildShipmentRouteCorridorFeatureCollection} feeds to computeCorridorBuffers.
 */
export async function computeShipmentCorridorInputHash(
  segments: ShipmentRouteBatchRow[],
  shipmentId: number,
  algorithmVersion: number,
  qualityPercent = 100,
): Promise<string> {
  const forShipment = orderedShipmentRouteSegments(segments, shipmentId);

  /** Order-independent fingerprint of API route geometry + width (same multiset → same hash). */
  const paths: {
    routeId: number;
    polyline: string;
    width: number;
    qualityPercent: number;
  }[] = [];

  for (const seg of forShipment) {
    const opts = seg.route_options ?? [];
    const width = seg.corridor_width_meters;
    for (const opt of opts) {
      if (!opt?.encoded_polyline || width == null || width <= 0) continue;
      paths.push({
        routeId: seg.id,
        polyline: opt.encoded_polyline,
        width,
        qualityPercent,
      });
    }
  }

  paths.sort((a, b) => {
    if (a.routeId !== b.routeId) return a.routeId - b.routeId;
    const p = a.polyline.localeCompare(b.polyline);
    if (p !== 0) return p;
    if (a.width !== b.width) return a.width - b.width;
    return a.qualityPercent - b.qualityPercent;
  });

  const payload = JSON.stringify({ alg: algorithmVersion, paths });
  const buf = new TextEncoder().encode(payload);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
